// 设置服务 - 管理全局设置
import embeddedKeyManager, { EMBEDDED_DEEPSEEK_PLACEHOLDER } from '../utils/embeddedKeyManager'

class SettingsService {
  constructor() {
    this.defaultSettings = {
      languageModel: 'gemini-2.5-pro',
      languageApiKey: '',
      languageApiUrl: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-pro:generateContent',
      imageModel: 'doubao-seedream-4-0-250828',
      imageApiKey: '',
      imageApiUrl: '/api/doubao', // 使用Vite代理
      autoSave: true,
      darkMode: true,
      language: 'zh-CN'
    }
    this.settings = this.loadSettings()
    this.injectEmbeddedKeys()
  }

  // 加载设置
  loadSettings() {
    try {
      const savedSettings = localStorage.getItem('altverse-settings')
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings)
        const mergedSettings = { ...this.defaultSettings, ...parsed }
        
        // 迁移设置：如果使用豆包模型但API URL不是代理地址，则自动更新
        if ((mergedSettings.imageModel === 'doubao-seedream-4-0' || mergedSettings.imageModel === 'doubao-seedream-4-0-250828') && 
            mergedSettings.imageApiUrl !== '/api/doubao') {
          console.log('🔄 自动迁移豆包设置到Vite代理')
          mergedSettings.imageApiUrl = '/api/doubao'
          // 确保使用完整的模型名称
          mergedSettings.imageModel = 'doubao-seedream-4-0-250828'
          // 自动保存迁移后的设置
          this.saveSettings(mergedSettings)
        }
        
        // 迁移旧的豆包模型名称
        if (mergedSettings.imageModel === 'doubao-seedream-4-0') {
          console.log('🔄 自动迁移豆包模型名称到完整版本')
          mergedSettings.imageModel = 'doubao-seedream-4-0-250828'
          // 自动保存迁移后的设置
          this.saveSettings(mergedSettings)
        }
        
        return mergedSettings
      }
    } catch (error) {
      console.error('加载设置失败:', error)
    }
    return { ...this.defaultSettings }
  }

  // 保存设置
  saveSettings(settings) {
    try {
      this.settings = { ...this.settings, ...settings }
      this.injectEmbeddedKeys()
      localStorage.setItem('altverse-settings', JSON.stringify(this.settings))
      return true
    } catch (error) {
      console.error('保存设置失败:', error)
      return false
    }
  }

  // 获取设置
  getSettings() {
    return { ...this.settings }
  }

  // 获取特定设置
  getSetting(key) {
    return this.settings[key]
  }

  // 更新设置
  updateSetting(key, value) {
    this.settings[key] = value
    if (key === 'languageModel' || key === 'languageApiKey' || key === 'languageApiUrl') {
      this.injectEmbeddedKeys()
    }
    return this.saveSettings({ [key]: value })
  }

  // 重置设置
  resetSettings() {
    this.settings = { ...this.defaultSettings }
    localStorage.removeItem('altverse-settings')
    this.injectEmbeddedKeys()
    return true
  }

  // 获取语言大模型配置
  injectEmbeddedKeys() {
    const usesDeepseek = (this.settings.languageModel || '').includes('deepseek')
    const embeddedKey = embeddedKeyManager.getDeepseekKey()

    if (usesDeepseek && embeddedKey) {
      if (!this.settings.languageApiKey || embeddedKeyManager.isPlaceholder(this.settings.languageApiKey)) {
        this.settings.languageApiKey = EMBEDDED_DEEPSEEK_PLACEHOLDER
      }
      if (!this.settings.languageApiUrl || !this.settings.languageApiUrl.includes('deepseek')) {
        this.settings.languageApiUrl = 'https://api.deepseek.com/v1/chat/completions'
      }
    } else if (!usesDeepseek && embeddedKeyManager.isPlaceholder(this.settings.languageApiKey)) {
      this.settings.languageApiKey = ''
    }
  }

  getLanguageModelConfig() {
    const config = {
      model: this.settings.languageModel,
      apiKey: this.settings.languageApiKey,
      apiUrl: this.settings.languageApiUrl
    }

    if ((config.model || '').includes('deepseek')) {
      if (!config.apiUrl || !config.apiUrl.includes('deepseek')) {
        config.apiUrl = 'https://api.deepseek.com/v1/chat/completions'
      }

      if (!config.apiKey || embeddedKeyManager.isPlaceholder(config.apiKey)) {
        const embeddedKey = embeddedKeyManager.getDeepseekKey()
        if (embeddedKey) {
          config.apiKey = embeddedKey
        }
      }
    }

    return config
  }

  // 获取文生图配置
  getImageGenerationConfig() {
    return {
      model: this.settings.imageModel,
      apiKey: this.settings.imageApiKey,
      apiUrl: this.settings.imageApiUrl
    }
  }




  // 验证语言模型API配置
  validateLanguageApiConfig() {
    const config = this.getLanguageModelConfig()
    return {
      isValid: !!(config.apiKey && config.apiUrl),
      missing: [
        !config.apiKey && 'API Key',
        !config.apiUrl && 'API URL'
      ].filter(Boolean)
    }
  }

  // 验证文生图API配置
  validateImageApiConfig() {
    const config = this.getImageGenerationConfig()
    return {
      isValid: !!(config.apiKey && config.apiUrl),
      missing: [
        !config.apiKey && 'API Key',
        !config.apiUrl && 'API URL'
      ].filter(Boolean)
    }
  }



  // 验证所有API配置
  validateAllApiConfigs() {
    return {
      language: this.validateLanguageApiConfig(),
      image: this.validateImageApiConfig(),
      allValid: this.validateLanguageApiConfig().isValid && this.validateImageApiConfig().isValid
    }
  }

  // 测试语言模型API连接
  async testLanguageApiConnection() {
    const config = this.getLanguageModelConfig()
    if (!config.apiKey || !config.apiUrl) {
      return { success: false, error: 'API Key或API URL未配置' }
    }

    try {
      let headers = {
        'Content-Type': 'application/json'
      }
      let requestBody

      if (config.model.includes('gemini')) {
        // Gemini API格式
        headers['X-Goog-Api-Key'] = config.apiKey
        requestBody = {
          contents: [{
            parts: [{
              text: 'Hello, this is a connection test.'
            }]
          }]
        }
      } else {
        // 其他API格式
        headers['Authorization'] = `Bearer ${config.apiKey}`
        requestBody = {
          model: config.model,
          messages: [{ role: 'user', content: 'Hello, this is a connection test.' }],
          max_tokens: 10
        }
      }

      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      })

      if (response.ok) {
        return { success: true, message: '连接成功' }
      } else {
        const errorData = await response.json().catch(() => ({}))
        let errorMessage = `连接失败: ${response.status} ${errorData.error?.message || response.statusText}`
        
        // 提供更友好的错误信息
        if (response.status === 429) {
          errorMessage = 'API配额已用完。请检查你的Google AI Studio配额或稍后重试。'
        } else if (response.status === 401) {
          errorMessage = 'API Key无效。请检查你的Google AI Studio API Key是否正确。'
        } else if (response.status === 403) {
          errorMessage = 'API访问被拒绝。请检查你的API Key权限。'
        }
        
        return { success: false, error: errorMessage }
      }
    } catch (error) {
      return { success: false, error: `连接错误: ${error.message}` }
    }
  }

  // 测试图像生成API连接
  async testImageApiConnection() {
    const config = this.getImageGenerationConfig()
    if (!config.apiKey || !config.apiUrl) {
      return { success: false, error: 'API Key或API URL未配置' }
    }

    try {
      // 对于豆包-seedream-4-0模型
      if (config.model === 'doubao-seedream-4-0' || config.model === 'doubao-seedream-4-0-250828') {
        const response = await fetch(config.apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${config.apiKey}`
          },
          body: JSON.stringify({
            model: "doubao-seedream-4-0-250828",
            prompt: "一个简单的红色圆圈，白色背景，测试图片",
            sequential_image_generation: "auto",
            sequential_image_generation_options: {
              max_images: 1
            },
            response_format: "url",
            size: "1K",
            stream: false,
            watermark: true
          })
        })

        if (response.ok) {
          return { success: true, message: '豆包API连接成功' }
        } else {
          const errorData = await response.json().catch(() => ({}))
          let errorMessage = `豆包API连接失败: ${response.status} ${errorData.error?.message || response.statusText}`
          
          // 提供更友好的错误信息
          if (response.status === 429) {
            errorMessage = '豆包API配额已用完。请检查你的豆包AI配额或稍后重试。'
          } else if (response.status === 401) {
            errorMessage = '豆包API Key无效。请检查你的豆包AI API Key是否正确。'
          } else if (response.status === 403) {
            errorMessage = '豆包API访问被拒绝。请检查你的API Key权限。'
          }
          
          return { success: false, error: errorMessage }
        }
      }

      // 对于ComfyUI本地模型，使用不同的测试方式
      if (config.model === 'comfyui-local') {
        const response = await fetch(config.apiUrl.replace('/prompt', '/system_stats'), {
          method: 'GET'
        })
        if (response.ok) {
          return { success: true, message: 'ComfyUI连接成功' }
        } else {
          return { success: false, error: `ComfyUI连接失败: ${response.status}` }
        }
      }


      // 对于Gemini图像生成
      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': config.apiKey
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: 'Generate a simple test image: a red circle on white background'
            }]
          }]
        })
      })

      if (response.ok) {
        return { success: true, message: '连接成功' }
      } else {
        const errorData = await response.json().catch(() => ({}))
        let errorMessage = `连接失败: ${response.status} ${errorData.error?.message || response.statusText}`
        
        // 提供更友好的错误信息
        if (response.status === 429) {
          errorMessage = 'API配额已用完。请检查你的Google AI Studio配额或稍后重试。'
        } else if (response.status === 401) {
          errorMessage = 'API Key无效。请检查你的Google AI Studio API Key是否正确。'
        } else if (response.status === 403) {
          errorMessage = 'API访问被拒绝。请检查你的API Key权限。'
        }
        
        return { success: false, error: errorMessage }
      }
    } catch (error) {
      return { success: false, error: `连接错误: ${error.message}` }
    }
  }


}

// 创建单例实例
const settingsService = new SettingsService()

export default settingsService
